import React, { useState } from 'react';
import Head from 'next/head';
import { validatePhoneNumber } from '@/lib/validation/phoneValidator';
import InlineEmailOtpVerification from '@/components/common/InlineEmailOtpVerification';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    mobileNumber: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (verifiedEmail !== formData.email.trim().toLowerCase()) {
      setStatus('error');
      setErrorMessage('Please verify your email with OTP before sending your inquiry.');
      return;
    }

    const phoneValidation = validatePhoneNumber(
      formData.countryCode,
      formData.mobileNumber,
    );

    if (!phoneValidation.valid) {
      setStatus('error');
      setErrorMessage(phoneValidation.error);
      return;
    }

    setStatus('sending');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          countryCode: phoneValidation.countryCode,
          mobileNumber: phoneValidation.mobileNumber,
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          countryCode: '+91',
          mobileNumber: '',
          subject: '',
          message: '',
        });
      } else {
        setStatus('error');
        setErrorMessage(data.message || data.suggestion || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <>
      <Head>
        <title>Contact Us | L.I.S.T Learning Pathways</title>
        <meta name="description" content="Get in touch with the L.I.S.T team for program inquiries and corporate solutions." />
      </Head>

      <main className="min-h-screen bg-white">
        {/* Header Section */}
        <section className="relative overflow-hidden bg-brand-dark pt-20 pb-16 lg:pt-32 lg:pb-20">
          <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[400px] h-[400px] bg-brand-primary/10 rounded-full blur-3xl"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
            <span className="inline-block px-4 py-1.5 bg-brand-primary/20 text-white text-xs font-bold rounded-full mb-4">
              Get in touch
            </span>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-[1.1] tracking-tight">
              Begin Your <span className="text-brand-primary">Evolution.</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl leading-relaxed">
              Whether you are an individual professional or a corporate partner, we are ready to discuss your leadership journey.
            </p>
          </div>
        </section>

        {/* Contact Grid */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">

              {/* Form Block */}
              <div className="order-1 lg:order-2 bg-gray-50/50 p-6 rounded-md border border-gray-100 shadow-sm">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-sm font-bold text-brand-dark block mb-2">Your Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                        className="w-full bg-white border border-gray-200 rounded-md py-3 px-4 text-brand-dark font-medium focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/10 focus:outline-none transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-brand-dark block mb-2">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        className="w-full bg-white border border-gray-200 rounded-md py-3 px-4 text-brand-dark font-medium focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/10 focus:outline-none transition-all text-sm"
                      />
                      <div className="mt-3">
                        <InlineEmailOtpVerification
                          email={formData.email}
                          verifiedEmail={verifiedEmail}
                          onVerifiedEmailChange={setVerifiedEmail}
                          context="contact"
                          mode="manual"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-[140px,1fr] gap-5">
                    <div>
                      <label className="text-sm font-bold text-brand-dark block mb-2">Country Code</label>
                      <input
                        type="text"
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleChange}
                        required
                        placeholder="+91"
                        className="w-full bg-white border border-gray-200 rounded-md py-3 px-4 text-brand-dark font-medium focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/10 focus:outline-none transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-brand-dark block mb-2">Mobile Number</label>
                      <input
                        type="tel"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleChange}
                        required
                        inputMode="numeric"
                        placeholder="9876543210"
                        className="w-full bg-white border border-gray-200 rounded-md py-3 px-4 text-brand-dark font-medium focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/10 focus:outline-none transition-all text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-bold text-brand-dark block mb-2">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="Program Inquiry"
                      className="w-full bg-white border border-gray-200 rounded-md py-3 px-4 text-brand-dark font-medium focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/10 focus:outline-none transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-bold text-brand-dark block mb-2">Your Message</label>
                    <textarea
                      name="message"
                      rows="4"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="How can we help you?"
                      className="w-full bg-white border border-gray-200 rounded-md py-3 px-4 text-brand-dark font-medium focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/10 focus:outline-none transition-all resize-none text-sm"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'sending' || verifiedEmail !== formData.email.trim().toLowerCase()}
                    className={`w-full py-3 bg-brand-primary text-white font-bold rounded-md shadow-lg shadow-brand-primary/20 hover:shadow-brand-primary/40 transition-all active:scale-95 text-sm ${status === 'sending' ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {status === 'sending' ? 'Sending Inquiry...' : 'Send Message'}
                  </button>

                  {status === 'success' && (
                    <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-md text-emerald-700 text-sm font-bold text-center">
                      Message sent successfully. We will be in touch soon.
                    </div>
                  )}
                  {status === 'error' && (
                    <div className="p-4 bg-rose-50 border border-rose-100 rounded-md text-rose-700 text-sm font-bold text-center">
                      {errorMessage || 'Something went wrong. Please try again.'}
                    </div>
                  )}
                </form>
              </div>

              {/* Information Block */}
              <div className="order-2 lg:order-1 py-4">
                <span className="text-brand-primary font-bold text-xs mb-8 block">Contact Information</span>

                <div className="space-y-8">
                  <div className="group mb-8">
                    <h4 className="text-sm font-bold text-brand-primary mb-3">India Hub</h4>
                    <p className="text-lg font-bold text-brand-dark leading-snug group-hover:text-brand-primary transition-colors">
                      405, A wing, Samartha Aishwarya,<br />
                      Lokhandwala, Andheri (West),<br />
                      Mumbai - 400053.
                    </p>
                  </div>

                  <div className="group">
                    <h4 className="text-sm font-bold text-brand-primary mb-3">UAE Hub</h4>
                    <p className="text-lg font-bold text-brand-dark leading-snug group-hover:text-brand-primary transition-colors">
                      306, White Crown Building,<br />
                      Trade Center 1, Sheikh Zayed Rd,<br />
                      Dubai.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-8 mt-8">
                    <div className="group">
                      <h4 className="text-sm font-bold text-brand-primary mb-3">Email Us</h4>
                      <a href="mailto:connect@list.academy" className="text-base font-bold text-brand-dark hover:text-brand-primary transition-all">
                        connect@list.academy
                      </a>
                    </div>
                    <div className="group">
                      <h4 className="text-sm font-bold text-brand-primary mb-3">Call Us</h4>
                      <div className="flex flex-col gap-1">
                        <a href="tel:+918454944090" className="text-base font-bold text-brand-dark hover:text-brand-primary transition-all">
                          +91 84549 44090
                        </a>
                        <a href="tel:+971501216802" className="text-base font-bold text-brand-dark hover:text-brand-primary transition-all">
                          +971 50 121 6802
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h4 className="text-sm font-bold text-brand-primary mb-4">Follow Our Journey</h4>
                    <div className="flex items-center gap-4">
                      <a
                        href="#"
                        className="w-10 h-10 bg-gray-50 rounded-full flex items-center justify-center text-brand-dark hover:bg-brand-primary hover:text-white transition-all duration-300"
                        aria-label="Facebook"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                      </a>
                      <a
                        href="#"
                        className="w-10 h-10 bg-gray-50 rounded-full flex items-center justify-center text-brand-dark hover:bg-brand-primary hover:text-white transition-all duration-300"
                        aria-label="LinkedIn"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                      </a>
                      <a
                        href="#"
                        className="w-10 h-10 bg-gray-50 rounded-full flex items-center justify-center text-brand-dark hover:bg-brand-primary hover:text-white transition-all duration-300"
                        aria-label="Instagram"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                        </svg>
                      </a>
                      <a
                        href="#"
                        className="w-10 h-10 bg-gray-50 rounded-full flex items-center justify-center text-brand-dark hover:bg-brand-primary hover:text-white transition-all duration-300"
                        aria-label="YouTube"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>
    </>
  );
}
